import {
  doc,
  Firestore,
  runTransaction,
  Timestamp,
} from 'firebase/firestore'
import { User } from '../core/providers/auth'

export type BookingErrorCode =
  | 'missing-user'
  | 'missing-workout'
  | 'missing-credit-balance'
  | 'insufficient-credits'
  | 'workout-full'
  | 'already-booked'
  | 'missing-waitlist-entry'
  | 'not-first-in-waitlist'
  | 'booking-failed'

export class BookingError extends Error {
  code: BookingErrorCode

  constructor(code: BookingErrorCode, message: string) {
    super(message)
    this.code = code
  }
}

export type BookingResult = {
  booked: boolean
  creditsBalance: number
  creditsDelta: number
}

export type BookingOptions = {
  creditsEnabled?: boolean
  participantsCount?: number
  numberOfPlaces?: number
}

export type WaitlistResult = {
  waitlisted: boolean
}

export type ClaimWaitlistOptions = BookingOptions & {
  waitlistPosition?: number
}

export type WaitlistBookingEntry = {
  uid: string
  displayName: string
  email: string
  phone: string
}

const numberOrZero = (value: unknown) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const hasCapacity = (options: BookingOptions) => {
  if (
    typeof options.participantsCount !== 'number' ||
    typeof options.numberOfPlaces !== 'number'
  ) {
    return true
  }

  return options.participantsCount < options.numberOfPlaces
}

const participantPayload = (user: User, cost: number) => ({
  uid: user.uid,
  displayName: user.displayName,
  email: user.email,
  phone: user.phoneNumber,
  createdAt: Timestamp.now(),
  creditsCharged: cost,
})

const waitlistPayload = (user: User) => ({
  uid: user.uid,
  displayName: user.displayName,
  email: user.email,
  phone: user.phoneNumber,
  createdAt: Timestamp.now(),
})

const participantPayloadFromWaitlist = (
  entry: WaitlistBookingEntry,
  cost: number
) => ({
  uid: entry.uid,
  displayName: entry.displayName,
  email: entry.email,
  phone: entry.phone,
  createdAt: Timestamp.now(),
  creditsCharged: cost,
})

export async function toggleWorkoutBooking(
  firestore: Firestore,
  workoutId: string,
  user: User | null,
  options: BookingOptions = {}
): Promise<BookingResult> {
  if (!user) {
    throw new BookingError(
      'missing-user',
      'Du behöver vara inloggad för att boka pass.'
    )
  }

  const workoutRef = doc(firestore, 'workouts', workoutId)
  const userRef = doc(firestore, 'users', user.uid)
  const participantRef = doc(
    firestore,
    'workouts',
    workoutId,
    'participants',
    user.uid
  )

  return runTransaction(firestore, async (transaction) => {
    const workoutSnap = await transaction.get(workoutRef)
    if (!workoutSnap.exists()) {
      throw new BookingError('missing-workout', 'Passet finns inte längre.')
    }

    const userSnap = await transaction.get(userRef)
    const participantSnap = await transaction.get(participantRef)
    const workout = workoutSnap.data()
    const cost = options.creditsEnabled === false
      ? 0
      : Math.max(0, numberOrZero(workout.credits))
    const creditsBalance = numberOrZero(userSnap.data()?.credits)

    if (participantSnap.exists()) {
      const creditsCharged = Math.max(
        0,
        numberOrZero(participantSnap.data().creditsCharged)
      )
      const nextBalance = creditsBalance + creditsCharged

      transaction.delete(participantRef)

      if (creditsCharged > 0) {
        if (!userSnap.exists()) {
          throw new BookingError(
            'missing-credit-balance',
            'Kunde inte hitta ditt creditsaldo.'
          )
        }

        transaction.update(userRef, {
          credits: nextBalance,
          lastCreditWorkoutId: workoutId,
          lastCreditAction: 'cancel',
          lastCreditUpdatedAt: Timestamp.now(),
        })
      }

      return {
        booked: false,
        creditsBalance: nextBalance,
        creditsDelta: creditsCharged,
      }
    }

    if (!hasCapacity(options)) {
      throw new BookingError(
        'workout-full',
        'Passet är fullt. Du kan ställa dig i kö.'
      )
    }

    if (cost > 0 && !userSnap.exists()) {
      throw new BookingError(
        'missing-credit-balance',
        'Kunde inte hitta ditt creditsaldo.'
      )
    }

    if (creditsBalance < cost) {
      throw new BookingError(
        'insufficient-credits',
        `Du behöver ${cost} credits för att boka passet. Ditt saldo är ${creditsBalance}.`
      )
    }

    const nextBalance = creditsBalance - cost

    transaction.set(participantRef, participantPayload(user, cost))

    if (cost > 0) {
      transaction.update(userRef, {
        credits: nextBalance,
        lastCreditWorkoutId: workoutId,
        lastCreditAction: 'book',
        lastCreditUpdatedAt: Timestamp.now(),
      })
    }

    return {
      booked: true,
      creditsBalance: nextBalance,
      creditsDelta: cost,
    }
  })
}

export async function joinWorkoutWaitlist(
  firestore: Firestore,
  workoutId: string,
  user: User | null,
  options: BookingOptions = {}
): Promise<WaitlistResult> {
  if (!user) {
    throw new BookingError(
      'missing-user',
      'Du behöver vara inloggad för att ställa dig i kö.'
    )
  }

  const workoutRef = doc(firestore, 'workouts', workoutId)
  const userRef = doc(firestore, 'users', user.uid)
  const participantRef = doc(
    firestore,
    'workouts',
    workoutId,
    'participants',
    user.uid
  )
  const waitlistRef = doc(
    firestore,
    'workouts',
    workoutId,
    'waitlist',
    user.uid
  )

  return runTransaction(firestore, async (transaction) => {
    const workoutSnap = await transaction.get(workoutRef)
    if (!workoutSnap.exists()) {
      throw new BookingError('missing-workout', 'Passet finns inte längre.')
    }

    const userSnap = await transaction.get(userRef)
    const participantSnap = await transaction.get(participantRef)
    const waitlistSnap = await transaction.get(waitlistRef)
    const workout = workoutSnap.data()
    const cost =
      options.creditsEnabled === false
        ? 0
        : Math.max(0, numberOrZero(workout.credits))
    const creditsBalance = numberOrZero(userSnap.data()?.credits)

    if (participantSnap.exists()) {
      throw new BookingError('already-booked', 'Du är redan bokad på passet.')
    }

    if (cost > 0 && !userSnap.exists()) {
      throw new BookingError(
        'missing-credit-balance',
        'Kunde inte hitta ditt creditsaldo.'
      )
    }

    if (creditsBalance < cost) {
      throw new BookingError(
        'insufficient-credits',
        `Du behöver ${cost} credits för att köa till passet. Ditt saldo är ${creditsBalance}.`
      )
    }

    if (!waitlistSnap.exists()) {
      transaction.set(waitlistRef, waitlistPayload(user))
    }

    return { waitlisted: true }
  })
}

export async function leaveWorkoutWaitlist(
  firestore: Firestore,
  workoutId: string,
  user: User | null
): Promise<WaitlistResult> {
  if (!user) {
    throw new BookingError(
      'missing-user',
      'Du behöver vara inloggad för att lämna kön.'
    )
  }

  const waitlistRef = doc(
    firestore,
    'workouts',
    workoutId,
    'waitlist',
    user.uid
  )

  return runTransaction(firestore, async (transaction) => {
    const waitlistSnap = await transaction.get(waitlistRef)

    if (waitlistSnap.exists()) {
      transaction.delete(waitlistRef)
    }

    return { waitlisted: false }
  })
}

export async function claimWorkoutWaitlistSpot(
  firestore: Firestore,
  workoutId: string,
  user: User | null,
  options: ClaimWaitlistOptions = {}
): Promise<BookingResult> {
  if (!user) {
    throw new BookingError(
      'missing-user',
      'Du behöver vara inloggad för att boka pass.'
    )
  }

  if (!hasCapacity(options)) {
    throw new BookingError(
      'workout-full',
      'Passet är fortfarande fullt. Du står kvar i kön.'
    )
  }

  if (options.waitlistPosition && options.waitlistPosition > 1) {
    throw new BookingError(
      'not-first-in-waitlist',
      'Du behöver vara först i kön för att ta den lediga platsen.'
    )
  }

  const workoutRef = doc(firestore, 'workouts', workoutId)
  const userRef = doc(firestore, 'users', user.uid)
  const participantRef = doc(
    firestore,
    'workouts',
    workoutId,
    'participants',
    user.uid
  )
  const waitlistRef = doc(
    firestore,
    'workouts',
    workoutId,
    'waitlist',
    user.uid
  )

  return runTransaction(firestore, async (transaction) => {
    const workoutSnap = await transaction.get(workoutRef)
    if (!workoutSnap.exists()) {
      throw new BookingError('missing-workout', 'Passet finns inte längre.')
    }

    const userSnap = await transaction.get(userRef)
    const participantSnap = await transaction.get(participantRef)
    const waitlistSnap = await transaction.get(waitlistRef)
    const workout = workoutSnap.data()
    const cost =
      options.creditsEnabled === false
        ? 0
        : Math.max(0, numberOrZero(workout.credits))
    const creditsBalance = numberOrZero(userSnap.data()?.credits)

    if (!waitlistSnap.exists()) {
      throw new BookingError(
        'missing-waitlist-entry',
        'Du står inte längre i kön.'
      )
    }

    if (participantSnap.exists()) {
      transaction.delete(waitlistRef)
      return {
        booked: true,
        creditsBalance,
        creditsDelta: 0,
      }
    }

    if (cost > 0 && !userSnap.exists()) {
      throw new BookingError(
        'missing-credit-balance',
        'Kunde inte hitta ditt creditsaldo.'
      )
    }

    if (creditsBalance < cost) {
      throw new BookingError(
        'insufficient-credits',
        `Du behöver ${cost} credits för att boka passet. Ditt saldo är ${creditsBalance}.`
      )
    }

    const nextBalance = creditsBalance - cost

    transaction.set(participantRef, participantPayload(user, cost))
    transaction.delete(waitlistRef)

    if (cost > 0) {
      transaction.update(userRef, {
        credits: nextBalance,
        lastCreditWorkoutId: workoutId,
        lastCreditAction: 'book',
        lastCreditUpdatedAt: Timestamp.now(),
      })
    }

    return {
      booked: true,
      creditsBalance: nextBalance,
      creditsDelta: cost,
    }
  })
}

export async function promoteWaitlistEntryToParticipant(
  firestore: Firestore,
  workoutId: string,
  entry: WaitlistBookingEntry,
  options: BookingOptions = {}
): Promise<BookingResult> {
  if (!hasCapacity(options)) {
    throw new BookingError(
      'workout-full',
      'Passet är fortfarande fullt.'
    )
  }

  const workoutRef = doc(firestore, 'workouts', workoutId)
  const userRef = doc(firestore, 'users', entry.uid)
  const participantRef = doc(
    firestore,
    'workouts',
    workoutId,
    'participants',
    entry.uid
  )
  const waitlistRef = doc(
    firestore,
    'workouts',
    workoutId,
    'waitlist',
    entry.uid
  )

  return runTransaction(firestore, async (transaction) => {
    const workoutSnap = await transaction.get(workoutRef)
    if (!workoutSnap.exists()) {
      throw new BookingError('missing-workout', 'Passet finns inte längre.')
    }

    const userSnap = await transaction.get(userRef)
    const participantSnap = await transaction.get(participantRef)
    const waitlistSnap = await transaction.get(waitlistRef)
    const workout = workoutSnap.data()
    const cost =
      options.creditsEnabled === false
        ? 0
        : Math.max(0, numberOrZero(workout.credits))
    const creditsBalance = numberOrZero(userSnap.data()?.credits)

    if (!waitlistSnap.exists()) {
      throw new BookingError(
        'missing-waitlist-entry',
        'Köposten finns inte längre.'
      )
    }

    if (participantSnap.exists()) {
      transaction.delete(waitlistRef)
      return {
        booked: true,
        creditsBalance,
        creditsDelta: 0,
      }
    }

    if (cost > 0 && !userSnap.exists()) {
      throw new BookingError(
        'missing-credit-balance',
        'Kunde inte hitta användarens creditsaldo.'
      )
    }

    if (creditsBalance < cost) {
      throw new BookingError(
        'insufficient-credits',
        `${entry.displayName} saknar credits för passet.`
      )
    }

    const nextBalance = creditsBalance - cost

    transaction.set(participantRef, participantPayloadFromWaitlist(entry, cost))
    transaction.delete(waitlistRef)

    if (cost > 0) {
      transaction.update(userRef, {
        credits: nextBalance,
        lastCreditWorkoutId: workoutId,
        lastCreditAction: 'book',
        lastCreditUpdatedAt: Timestamp.now(),
      })
    }

    return {
      booked: true,
      creditsBalance: nextBalance,
      creditsDelta: cost,
    }
  })
}
